// src/shared/components/ui/ExpandableTable.tsx
import React, { useState } from 'react';
import type { MinimalisticTableProps } from './MinimalisticTable';
import type { ColumnDef } from './MinimalisticTable';
import ExpandToggle from './ExpandToggle';
import SearchBox from '../ui/SearchBox';

type ExpandableTableProps<RowType> = Omit<MinimalisticTableProps<RowType>, 'data'> & {
  data: RowType[];

  /** Given a row, return a unique key (string or number). */
  rowKey: (row: RowType) => string | number;

  /** Render the expanded‐row content when a row is open. */
  renderSubRow: (row: RowType) => React.ReactNode;

  /** Should we show the inline search box in the header? */
  showSearch?: boolean;
  /** Placeholder text for the search box. */
  searchPlaceholder?: string;
};

export default function ExpandableTable<RowType>({
  columns,
  data,
  noDataMessage = 'No data found.',
  showNewButton = true,
  onNew,
  editableAccessors,
  newRowValues,
  onNewRowChange,
  onAcceptNew,
  onCancelNew,

  rowKey,
  renderSubRow,

  showSearch = true,
  searchPlaceholder = 'Search…',
}: ExpandableTableProps<RowType>) {
  // ─── 1) Search State ───────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState<string>('');

  // ─── 2) Filter data based on searchTerm ────────────────────────────
  const filteredRows: RowType[] = React.useMemo(() => {
    if (!searchTerm) return data;
    const lower = searchTerm.toLowerCase();
    // Build a flat searchable text for each row (all accessors + derived Cell outputs best-effort)
    return data.filter((row) => {
      for (const col of columns) {
        // 1) Direct accessor value
        if (col.accessor) {
          const val = (row as any)[col.accessor];
          if (val != null && String(val).toLowerCase().includes(lower)) return true;
        }
        // 2) If custom Cell provided, attempt to extract plain text by calling it
        if (col.Cell) {
          try {
            const rendered = col.Cell(col.accessor ? (row as any)[col.accessor] : undefined, row);
            // Handle primitive or React element with children recursively (simple cases)
            const collectText = (node: any): string => {
              if (node == null || node === false) return '';
              if (typeof node === 'string' || typeof node === 'number') return String(node);
              if (Array.isArray(node)) return node.map(collectText).join(' ');
              if (typeof node === 'object') {
                // Common React element shape: { props: { children } }
                const ch = (node as any).props?.children;
                if (ch) return collectText(ch);
              }
              return '';
            };
            const txt = collectText(rendered).toLowerCase();
            if (txt && txt.includes(lower)) return true;
          } catch { /* ignore render extraction errors */ }
        }
      }
      return false;
    });
  }, [data, searchTerm, columns]);

  // ─── 3) Track expanded rows by key ─────────────────────────────────
  const [expandedKeys, setExpandedKeys] = useState<Set<string | number>>(new Set());
  const toggleRow = (key: string | number) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ─── 4) Flatten “main” + “sub” for rendering ────────────────────────
  type FlatRow =
    | { type: 'main'; row: RowType }
    | { type: 'sub'; row: RowType };

  const flatData: FlatRow[] = [];
  filteredRows.forEach((row) => {
    flatData.push({ type: 'main', row });
    const key = rowKey(row);
    if (expandedKeys.has(key)) {
      flatData.push({ type: 'sub', row });
    }
  });

  return (
    <table className="w-full text-left border-collapse">
      <thead>
        {/* ─── HEADER ROW: column titles, and in the last cell: [title + search + New] ───────── */}
        <tr>
          {columns.map((col, idx) => {
            const isLast = idx === columns.length - 1;
            const shouldShowButton = isLast && showNewButton && !!onNew;

            return (
              <th className="px-4 pt-2 pb-0 border-b border-neutral-700">
                {isLast ? (
                  // ─── LAST COLUMN: title + inline search + New button ─────────
                  <div className="flex items-center justify-between space-x-2">
                    <span>{col.header}</span>

                    <div className="flex items-center space-x-2">
                      {showSearch && (
                        <div className="w-48">
                          <SearchBox
                            value={searchTerm}
                            onChange={setSearchTerm}
                            placeholder={searchPlaceholder}
                          />
                        </div>
                      )}

                      {shouldShowButton && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onNew?.();
                          }}
                          className="bg-transparent text-emerald-500 px-2 py-1 hover:text-emerald-400"
                        >
                          New
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  // ─── NON‐LAST COLUMN: just the header title ─────────────────
                  <div className="flex items-end h-full justify-start">
                    <span>{col.header}</span>
                  </div>
                )}
              </th>
            );
          })}
        </tr>
      </thead>

      <tbody>
        {flatData.length === 0 ? (
          <tr>
            <td
              colSpan={columns.length}
              className="px-4 py-2 text-center text-neutral-500"
            >
              {noDataMessage}
            </td>
          </tr>
        ) : (
          flatData.map((flatRow, idx) => {
            if (flatRow.type === 'main') {
              const row = flatRow.row;
              const key = rowKey(row);
              const isExpanded = expandedKeys.has(key);
              const isNewRow = (row as any).__isNew === true;

              return (
                <tr
                  key={`main-${key}`}
                  className={`
                    border-b 
                    ${isExpanded ? 'border-neutral-950 bg-neutral-950' : 'border-neutral-800'} 
                    even:bg-neutral-900 
                    hover:bg-neutral-950 
                    group 
                    cursor-pointer
                  `}
                  onClick={() => {
                    if (!isNewRow) {
                      toggleRow(key);
                    }
                  }}
                >
                  {columns.map((col, colIdx) => {
                    const rawValue = col.accessor ? (row[col.accessor] as any) : undefined;

                    // ─── A) NEW‐ROW placeholder logic ─────────────────────
                    if (isNewRow) {
                      if (
                        editableAccessors &&
                        col.accessor &&
                        editableAccessors.includes(col.accessor)
                      ) {
                        return (
                          <td key={colIdx} className="px-4 py-2 align-top">
                            <input
                              type="text"
                              value={newRowValues?.[col.accessor] ?? ''}
                              onChange={(e) =>
                                onNewRowChange?.(col.accessor as any, e.target.value)
                              }
                              className="w-full bg-neutral-700 text-neutral-100 px-2 py-1 rounded"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </td>
                        );
                      }

                      if (colIdx === columns.length - 1) {
                        return (
                          <td key={colIdx} className="px-4 py-2 align-top">
                            <div className="flex space-x-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAcceptNew?.();
                                }}
                                className="text-emerald-500 hover:text-emerald-400"
                                disabled={!onNewRowChange}
                              >
                                Accept
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelNew?.();
                                }}
                                className="text-rose-500 hover:text-rose-400"
                              >
                                Cancel
                              </button>
                            </div>
                          </td>
                        );
                      }

                      return (
                        <td key={colIdx} className="px-4 py-2 align-top">
                          —{/* placeholder dash for non-editable fields */}
                        </td>
                      );
                    }

                    // ─── B) NORMAL ROW: expand‐toggle in first cell; Cell(...) or rawValue ──
                    if (colIdx === 0) {
                      // First column: show expand arrow + value
                      return (
                        <td key={colIdx} className="px-4 py-2 align-top flex items-center">
                          <ExpandToggle
                            isExpanded={isExpanded}
                            onToggle={(e) => {
                              e.stopPropagation();
                              toggleRow(key);
                            }}
                          />
                          <span className="ml-2">{rawValue}</span>
                        </td>
                      );
                    }

                    // Any other normal cell
                    if (col.Cell) {
                      return (
                        <td key={colIdx} className="px-4 py-2 align-top">
                          {/* If Cell renders clickable elements, they should call e.stopPropagation() */}
                          {col.Cell(rawValue, row)}
                        </td>
                      );
                    } else {
                      return (
                        <td key={colIdx} className="px-4 py-2 align-top">
                          {rawValue}
                        </td>
                      );
                    }
                  })}
                </tr>
              );
            } else {
              // ─── “sub” row: either injected extra <tr> rows or a single full-width cell ──
              const parentRow = flatRow.row;
              const key = rowKey(parentRow);
              const content = renderSubRow(parentRow);
              // If renderSubRow returned an array of <tr> elements, emit them directly.
              if (Array.isArray(content)) {
                return <React.Fragment key={`sub-${key}`}>{content}</React.Fragment>;
              }
              return (
                <tr key={`sub-${key}`}>
                  <td
                    colSpan={columns.length}
                    className="bg-neutral-950 px-4 py-2 border-b border-gray-800"
                  >
                    {content}
                  </td>
                </tr>
              );
            }
          })
        )}
      </tbody>
    </table>
  );
}
